import React, { useCallback, useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "@/hooks/useNavigate";
import { useSearchParams } from "@/hooks/useSearchParams";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import { EgyptianPhoneInput } from "@/components/ui/EgyptianPhoneInput";
import ClaimOtpStep from "@/components/ClaimOtpStep";
import PushSoftAsk from "@/components/notifications/PushSoftAsk";
import {
  searchStores,
  submitClaim,
  getMyClaims,
  type ClaimStoreResult,
  type MyClaim,
  type ProofImage,
} from "@/api/claimService";

// ClaimFlow — the public, authed 3-step store-claim funnel (MERCH-03 / D-06 / D-08).
// search → evidence → status. Lives outside the gated /merchant area; a pending claimant is
// NOT yet a merchant. Brand skin: RTL, Cairo, --ys-* tokens (inline styles matching
// ClaimBanner / ReviewCard). All /claims endpoints require auth, so the whole flow is
// login-gated (the /for-business landing has no wall; the login gate lives here).

type ClaimFlowProps = {
  // Store slug carried in from the ClaimBanner CTA (`/claim?store=<slug>`) — pre-selects and
  // skips to the evidence step when it resolves to a searchable store.
  initialStore?: string;
};

// "otp" sits between store selection and evidence: existing-store targets get offered the
// instant phone-OTP verification first and fall back to the manual evidence form. URL
// (store-not-found) targets skip it — there is no store record to hold a registered number.
type Step = "search" | "otp" | "evidence" | "status";

// The claim target: an existing store, or a store-not-found URL (D-08 auto-scrape).
type ClaimTarget =
  | { kind: "store"; store: ClaimStoreResult }
  | { kind: "url"; url: string };

const font = "var(--ys-font)";

const cardStyle: React.CSSProperties = {
  background: "var(--ys-paper)",
  border: "1px solid var(--ys-rule)",
  borderRadius: "var(--ys-r-md)",
  boxShadow: "var(--ys-shadow-2)",
  padding: 24,
  direction: "rtl",
  fontFamily: font,
  maxWidth: 560,
  margin: "32px auto 0px",
};

const headingStyle: React.CSSProperties = {
  fontFamily: font,
  fontSize: 20,
  fontWeight: 600,
  lineHeight: 1.25,
  color: "var(--ys-ink)",
  margin: 0,
};

const bodyStyle: React.CSSProperties = {
  fontFamily: font,
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.5,
  color: "var(--ys-ink-2)",
};

const labelStyle: React.CSSProperties = {
  fontFamily: font,
  fontSize: 16,
  fontWeight: 600,
  lineHeight: 1.4,
  color: "var(--ys-ink)",
  display: "block",
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: "var(--ys-r)",
  border: "1px solid var(--ys-rule-2)",
  background: "var(--ys-paper)",
  fontFamily: font,
  fontSize: 14,
  color: "var(--ys-ink)",
  boxSizing: "border-box",
};

const primaryBtnStyle: React.CSSProperties = {
  background: "var(--ys-primary)",
  color: "#fff",
  border: "none",
  borderRadius: "var(--ys-r)",
  padding: "12px 20px",
  minHeight: 44,
  fontFamily: font,
  fontSize: 16,
  fontWeight: 600,
  cursor: "pointer",
};

const ghostBtnStyle: React.CSSProperties = {
  background: "none",
  border: "1px solid var(--ys-rule-2)",
  borderRadius: "var(--ys-r)",
  padding: "10px 16px",
  minHeight: 44,
  fontFamily: font,
  fontSize: 14,
  fontWeight: 600,
  color: "var(--ys-ink-2)",
  cursor: "pointer",
};

const fieldHint: React.CSSProperties = {
  fontFamily: font,
  fontSize: 14,
  color: "var(--ys-ink-3)",
  fontWeight: 400,
};

// Soft pill marking an unpublished (scraped draft) store. Shared by the search results list and
// the evidence-step selected-store card so both call sites stay identical.
const draftPillStyle: React.CSSProperties = {
  display: "inline-block",
  background: "var(--ys-warning-soft)",
  color: "var(--ys-warning)",
  borderRadius: "var(--ys-r-pill)",
  padding: "2px 10px",
  fontSize: 12,
  fontWeight: 600,
  fontFamily: font,
};

const DRAFT_BADGE_LABEL = "غير منشور بعد";

const errorText: React.CSSProperties = {
  fontFamily: font,
  fontSize: 14,
  color: "var(--ys-danger)",
  marginTop: 4,
};

// Evidence form validation (frontend-only). Phone + pageUrl required; note optional.
const evidenceSchema = z.object({
  phone: z.string().trim().min(1, "رقم الهاتف مطلوب"),
  pageUrl: z.string().trim().min(1, "رابط صفحة المتجر مطلوب"),
  note: z.string().trim().optional(),
});

type EvidenceValues = z.infer<typeof evidenceSchema>;

const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function ClaimFlow({ initialStore }: ClaimFlowProps) {
  const { user, userCheckingFinished, showAuthModal } = useAuth();
  const navigate = useNavigate();
  const { trackClaim } = usePostHogTracking();
  const [searchParams] = useSearchParams();

  // claim_started fires once per mount. Entry attribution: explicit ?entry= (e.g. for_business)
  // wins, else a ?store= deep link means the ClaimBanner CTA, else direct.
  const startedTrackedRef = useRef(false);
  useEffect(() => {
    if (startedTrackedRef.current) return;
    startedTrackedRef.current = true;
    const entry = searchParams.get("entry") || (initialStore ? "store_link" : "direct");
    trackClaim.started({ entry, ...(initialStore ? { store_slug: initialStore } : {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [step, setStep] = useState<Step>("search");
  const [initDone, setInitDone] = useState(false);

  // Search state
  const [query, setQuery] = useState(initialStore ?? "");
  const [results, setResults] = useState<ClaimStoreResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [notFoundMode, setNotFoundMode] = useState(false); // "أضف متجري" URL entry revealed
  const [urlInput, setUrlInput] = useState("");

  // Claim target + status state
  const [target, setTarget] = useState<ClaimTarget | null>(null);
  const [myClaims, setMyClaims] = useState<MyClaim[]>([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  // True only when the status view was reached by submitting evidence in this session, so the
  // push soft ask follows the submission and never a plain revisit of «طلباتك».
  const [justSubmitted, setJustSubmitted] = useState(false);

  const runSearch = useCallback(async (q: string) => {
    setSearching(true);
    setSearchError(null);
    const res = await searchStores(q);
    setSearching(false);
    setSearched(true);
    if (res.ok) {
      setResults(res.data);
    } else {
      setResults([]);
      setSearchError(res.error);
    }
    return res;
  }, []);

  const loadMyClaims = useCallback(async () => {
    setStatusLoading(true);
    setStatusError(null);
    const res = await getMyClaims();
    setStatusLoading(false);
    if (res.ok) {
      setMyClaims(res.data);
      return res.data;
    }
    setStatusError(res.error);
    return [] as MyClaim[];
  }, []);

  // One-time init once auth is resolved: try to resolve initialStore → evidence; otherwise
  // if the user already has claims, land on the status view; else start at search.
  useEffect(() => {
    if (!userCheckingFinished || !user || initDone) return;
    let cancelled = false;
    (async () => {
      const claims = await loadMyClaims();
      if (cancelled) return;

      if (initialStore) {
        const res = await searchStores(initialStore);
        if (cancelled) return;
        if (res.ok) {
          // ONLY an exact slug match may auto-advance. A fuzzy top hit must never silently
          // lock the merchant onto a store that may not be theirs — they pick it explicitly.
          const match = res.data.find((s) => s.slug === initialStore);
          if (match) {
            trackClaim.storeSelected({ store_id: match.id, preselected: true });
            setTarget({ kind: "store", store: match });
            setStep("otp");
            setInitDone(true);
            return;
          }
          // No exact match — show the results and let the merchant choose. The search box is
          // already pre-filled because `query` is initialized to `initialStore ?? ""`.
          setResults(res.data);
          setSearched(true);
        }
        setStep("search");
        setInitDone(true);
        return;
      }

      if (claims.length > 0) {
        setStep("status");
      } else {
        setStep("search");
      }
      setInitDone(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [userCheckingFinished, user, initDone, initialStore, loadMyClaims, trackClaim]);

  // Debounced live search while on the search step.
  useEffect(() => {
    if (step !== "search") return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    const t = setTimeout(() => {
      runSearch(q);
    }, 300);
    return () => clearTimeout(t);
  }, [query, step, runSearch]);

  // claim_approved_viewed — once per mount when the status view shows an approved claim
  // (ref-guarded so status re-renders / claim refetches don't re-fire it).
  const approvedViewedRef = useRef(false);
  useEffect(() => {
    if (approvedViewedRef.current || step !== "status") return;
    const approved = myClaims.find((c) => c.status === "approved");
    if (!approved) return;
    approvedViewedRef.current = true;
    trackClaim.approvedViewed({ store_id: approved.storeId });
  }, [step, myClaims, trackClaim]);

  // ── Auth gate ──────────────────────────────────────────────────────────────
  if (!userCheckingFinished) {
    return (
      <div style={{ ...cardStyle, textAlign: "center" }}>
        <p style={bodyStyle}>جارِ التحميل…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={cardStyle}>
        <h2 style={headingStyle}>سجّل الدخول للمطالبة بمتجرك</h2>
        <p style={{ ...bodyStyle, marginTop: 8 }}>
          للمطالبة بمتجرك ومتابعة حالة الطلب، يلزم تسجيل الدخول أولاً.
        </p>
        <button
          type="button"
          style={{ ...primaryBtnStyle, marginTop: 16 }}
          onClick={() => showAuthModal({ trigger: "claim" })}
        >
          تسجيل الدخول
        </button>
      </div>
    );
  }

  // --- Step: search ---
  const renderSearch = () => {
    const q = query.trim();
    const showEmpty = searched && !searching && q.length >= 2 && results.length === 0;
    return (
      <div style={cardStyle}>
        <h2 style={headingStyle}>ابحث عن متجرك</h2>
        <p style={{ ...bodyStyle, marginTop: 8, marginBottom: 16 }}>
          ابحث عن متجرك بالاسم، واختره لبدء المطالبة بملكيته.
        </p>

        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setNotFoundMode(false);
          }}
          placeholder="اكتب اسم متجرك…"
          style={inputStyle}
          aria-label="اسم المتجر"
        />

        {searching && (
          <p style={{ ...fieldHint, marginTop: 12 }}>جارِ البحث…</p>
        )}

        {searchError && <p style={errorText}>{searchError}</p>}

        {results.length > 0 && (
          <ul style={{ listStyle: "none", padding: 0, margin: "16px 0 0" }}>
            {results.map((store) => (
              <li key={store.id} style={{ marginBottom: 8 }}>
                <button
                  type="button"
                  onClick={() => {
                    trackClaim.storeSelected({ store_id: store.id, preselected: false });
                    setTarget({ kind: "store", store });
                    setStep("otp");
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    width: "100%",
                    textAlign: "right",
                    background: "var(--ys-paper)",
                    border: "1px solid var(--ys-rule)",
                    borderRadius: "var(--ys-r)",
                    padding: 12,
                    cursor: "pointer",
                    fontFamily: font,
                  }}
                >
                  <img
                    src={store.logoUrl || "/placeholder.svg"}
                    alt=""
                    width={40}
                    height={40}
                    style={{
                      borderRadius: "var(--ys-r-pill)",
                      objectFit: "cover",
                      flexShrink: 0,
                      background: "var(--ys-bg-2)",
                    }}
                  />
                  <span style={{ flex: 1 }}>
                    <span
                      style={{
                        display: "block",
                        fontSize: 16,
                        fontWeight: 600,
                        color: "var(--ys-ink)",
                      }}
                    >
                      {store.name}
                    </span>
                    {store.isDraft && (
                      <span style={{ ...draftPillStyle, marginTop: 4 }}>
                        {DRAFT_BADGE_LABEL}
                      </span>
                    )}
                    <span
                      style={{
                        display: "block",
                        fontSize: 14,
                        color: "var(--ys-ink-3)",
                      }}
                    >
                      {store.reviewCount} تقييم
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {showEmpty && (
          <div style={{ marginTop: 16 }}>
            <p style={{ ...headingStyle, fontSize: 16 }}>لم نعثر على متجرك</p>
            <p style={{ ...bodyStyle, marginTop: 6 }}>
              أضف رابط صفحتك (فيسبوك / انستجرام / الموقع) وسنضيفه لك.
            </p>
            {!notFoundMode ? (
              <button
                type="button"
                style={{ ...primaryBtnStyle, marginTop: 12 }}
                onClick={() => {
                  setNotFoundMode(true);
                  setUrlInput("");
                }}
              >
                أضف متجري
              </button>
            ) : (
              <div style={{ marginTop: 12 }}>
                <input
                  type="url"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://facebook.com/yourstore"
                  dir="ltr"
                  style={{ ...inputStyle, textAlign: "left" }}
                  aria-label="رابط صفحة المتجر"
                />
                <button
                  type="button"
                  disabled={!urlInput.trim()}
                  style={{
                    ...primaryBtnStyle,
                    marginTop: 12,
                    opacity: urlInput.trim() ? 1 : 0.6,
                  }}
                  onClick={() => {
                    const url = urlInput.trim();
                    if (!url) return;
                    setTarget({ kind: "url", url });
                    setStep("evidence");
                  }}
                >
                  متابعة
                </button>
              </div>
            )}
          </div>
        )}

        {myClaims.length > 0 && (
          <button
            type="button"
            style={{ ...ghostBtnStyle, marginTop: 20 }}
            onClick={() => setStep("status")}
          >
            متابعة حالة طلباتي
          </button>
        )}
      </div>
    );
  };

  // --- Step: otp (instant verification via the store's registered number) ---
  const renderOtp = () => {
    if (target?.kind !== "store") {
      // URL targets never enter this step; render the evidence form if state drifts.
      return renderEvidence();
    }
    return (
      <ClaimOtpStep
        store={target.store}
        onFallbackToEvidence={() => setStep("evidence")}
        onVerified={async () => {
          await loadMyClaims();
          setStep("status");
        }}
        onBack={() => setStep("search")}
      />
    );
  };

  // --- Step: evidence ---
  const renderEvidence = () => (
    <EvidenceForm
      target={target}
      onBack={() => setStep("search")}
      onSubmitted={async () => {
        await loadMyClaims();
        setJustSubmitted(true);
        setStep("status");
      }}
    />
  );

  // --- Step: status ---
  const renderStatus = () => {
    if (statusLoading) {
      return (
        <div style={{ ...cardStyle, textAlign: "center" }}>
          <p style={bodyStyle}>جارِ التحميل…</p>
        </div>
      );
    }
    if (statusError) {
      return (
        <div style={cardStyle}>
          <p style={errorText}>{statusError}</p>
          <button
            type="button"
            style={{ ...ghostBtnStyle, marginTop: 12 }}
            onClick={() => loadMyClaims()}
          >
            إعادة المحاولة
          </button>
        </div>
      );
    }

    if (myClaims.length === 0) {
      return (
        <div style={cardStyle}>
          <h2 style={headingStyle}>لا توجد طلبات بعد</h2>
          <p style={{ ...bodyStyle, marginTop: 8 }}>
            ابدأ بالمطالبة بمتجرك لمتابعة حالته هنا.
          </p>
          <button
            type="button"
            style={{ ...primaryBtnStyle, marginTop: 16 }}
            onClick={() => setStep("search")}
          >
            طالب بمتجرك
          </button>
        </div>
      );
    }

    const pill = (bg: string, color: string, text: string) => (
      <span
        style={{
          display: "inline-block",
          background: bg,
          color,
          borderRadius: "var(--ys-r-pill)",
          padding: "4px 12px",
          fontFamily: font,
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {text}
      </span>
    );

    const statusPill = (status: MyClaim["status"]) => {
      switch (status) {
        case "pending":
          return pill("var(--ys-warning-soft)", "var(--ys-warning)", "قيد المراجعة");
        case "approved":
          return pill("var(--ys-success-soft)", "var(--ys-success)", "تمت الموافقة");
        case "rejected":
          return pill("var(--ys-danger-soft)", "var(--ys-danger)", "مرفوض");
        case "revoked":
          return pill("var(--ys-danger-soft)", "var(--ys-danger)", "تم إلغاء الملكية");
      }
    };

    const claimTitle = (claim: MyClaim) =>
      claim.store?.name || claim.evidence?.pageUrl || "متجر غير محدد";

    const formatClaimDate = (value?: string) => {
      if (!value) return "";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString("ar-EG", { year: "numeric", month: "long", day: "numeric" });
    };

    return (
      <div style={cardStyle}>
        <h2 style={headingStyle}>طلباتك</h2>

        <ul style={{ listStyle: "none", padding: 0, margin: "16px 0 0" }}>
          {myClaims.map((claim) => (
            <li
              key={claim.id}
              style={{
                border: "1px solid var(--ys-rule)",
                borderRadius: "var(--ys-r)",
                padding: 16,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <p style={{ ...bodyStyle, fontWeight: 600, color: "var(--ys-ink)", margin: 0 }}>
                    {claimTitle(claim)}
                  </p>
                  {claim.createdAt && (
                    <p style={{ ...fieldHint, marginTop: 4 }}>{formatClaimDate(claim.createdAt)}</p>
                  )}
                </div>
                {statusPill(claim.status)}
              </div>

              {claim.status === "pending" && (
                <p style={{ ...bodyStyle, marginTop: 10 }}>
                  سنراجع طلبك ونعلمك بالنتيجة قريباً.
                </p>
              )}

              {(claim.status === "rejected" || claim.status === "revoked") &&
                claim.rejectionReason && (
                  <p
                    style={{
                      ...bodyStyle,
                      marginTop: 10,
                      background: "var(--ys-danger-soft)",
                      color: "var(--ys-danger)",
                      borderRadius: "var(--ys-r-sm)",
                      padding: "8px 12px",
                    }}
                  >
                    السبب: {claim.rejectionReason}
                  </p>
                )}

              {claim.status === "approved" && (
                <button
                  type="button"
                  style={{ ...primaryBtnStyle, marginTop: 12 }}
                  onClick={() => navigate("/merchant")}
                >
                  اذهب إلى لوحة المتجر
                </button>
              )}
            </li>
          ))}
        </ul>

        <button
          type="button"
          style={{ ...ghostBtnStyle, marginTop: 8 }}
          onClick={() => {
            setTarget(null);
            setQuery("");
            setResults([]);
            setSearched(false);
            setNotFoundMode(false);
            setStep("search");
          }}
        >
          المطالبة بمتجر آخر
        </button>
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 560, margin: "0 auto" }}>
      {step === "search" && renderSearch()}
      {step === "otp" && renderOtp()}
      {step === "evidence" && renderEvidence()}
      {step === "status" && renderStatus()}
      {step === "status" && justSubmitted && (
        <div style={{ marginTop: 16 }}>
          <PushSoftAsk surface="claim_submitted" />
        </div>
      )}
    </div>
  );
}

// Evidence form (react-hook-form + zod). Handles both the existing-store branch (storeId)
// and the store-not-found branch (targetUrl); phone + pageUrl are always required.
function EvidenceForm({
  target,
  onBack,
  onSubmitted,
}: {
  target: ClaimTarget | null;
  onBack: () => void;
  onSubmitted: () => void | Promise<void>;
}) {
  const { trackClaim } = usePostHogTracking();
  const prefillUrl = target?.kind === "url" ? target.url : "";
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EvidenceValues>({
    resolver: zodResolver(evidenceSchema),
    defaultValues: { phone: "", pageUrl: prefillUrl, note: "" },
  });

  const [proofImage, setProofImage] = useState<ProofImage | null>(null);
  const [proofPreview, setProofPreview] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = await toBase64(file);
    setProofImage({ data, mimeType: file.type, fileName: file.name });
    setProofPreview(URL.createObjectURL(file));
  };

  const removeProof = () => {
    setProofImage(null);
    if (proofPreview) URL.revokeObjectURL(proofPreview);
    setProofPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onValid = async (values: EvidenceValues) => {
    const res = await submitClaim({
      storeId: target?.kind === "store" ? target.store.id : undefined,
      targetUrl: target?.kind === "url" ? target.url : undefined,
      evidence: {
        phone: values.phone,
        pageUrl: values.pageUrl,
        note: values.note?.trim() || undefined,
        proofImage: proofImage || undefined,
      },
    });
    if (res.ok) {
      trackClaim.evidenceSubmitted({
        ...(target?.kind === "store" ? { store_id: target.store.id } : {}),
        files_count: proofImage ? 1 : 0,
      });
      toast.success("تم إرسال طلبك للمراجعة");
      await onSubmitted();
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div style={cardStyle}>
      <h2 style={headingStyle}>أثبت ملكيتك للمتجر</h2>

      {/* Selected store shown as a card so the merchant can see exactly what they are claiming
          and bail out to search if it is not their store. URL targets stay a plain line. */}
      {target?.kind === "store" && (
        <div
          style={{
            border: "1px solid var(--ys-rule)",
            borderRadius: "var(--ys-r)",
            padding: 12,
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 12,
          }}
        >
          <img
            src={target.store.logoUrl || "/placeholder.svg"}
            alt=""
            width={40}
            height={40}
            style={{
              borderRadius: "var(--ys-r-pill)",
              objectFit: "cover",
              flexShrink: 0,
              background: "var(--ys-bg-2)",
            }}
          />
          <span style={{ flex: 1 }}>
            <span
              style={{
                display: "block",
                fontSize: 16,
                fontWeight: 600,
                color: "var(--ys-ink)",
              }}
            >
              {target.store.name}
            </span>
            {target.store.isDraft && (
              <span style={{ ...draftPillStyle, marginTop: 4 }}>
                {DRAFT_BADGE_LABEL}
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={onBack}
            disabled={isSubmitting}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              fontFamily: font,
              fontSize: 14,
              fontWeight: 600,
              color: "var(--ys-primary)",
              cursor: isSubmitting ? "not-allowed" : "pointer",
              opacity: isSubmitting ? 0.6 : 1,
              flexShrink: 0,
            }}
          >
            ليس متجرك؟
          </button>
        </div>
      )}

      {target?.kind === "url" && (
        <p style={{ ...bodyStyle, marginTop: 8 }}>
          المتجر:{" "}
          <strong style={{ color: "var(--ys-ink)" }} dir="ltr">
            {target.url}
          </strong>
        </p>
      )}

      <form onSubmit={handleSubmit(onValid)} style={{ marginTop: 16 }}>
        <div style={{ marginBottom: 16 }}>
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <EgyptianPhoneInput
                id="claim-phone"
                label="رقم الهاتف"
                value={field.value}
                onChange={field.onChange}
                error={errors.phone?.message}
                disabled={isSubmitting}
              />
            )}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label htmlFor="claim-pageUrl" style={labelStyle}>
            رابط صفحة المتجر (فيسبوك / انستجرام / الموقع)
          </label>
          <input
            id="claim-pageUrl"
            type="url"
            dir="ltr"
            placeholder="https://facebook.com/yourstore"
            style={{ ...inputStyle, textAlign: "left" }}
            disabled={isSubmitting}
            {...register("pageUrl")}
          />
          {errors.pageUrl && <p style={errorText}>{errors.pageUrl.message}</p>}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label htmlFor="claim-note" style={labelStyle}>
            ملاحظة <span style={fieldHint}>(اختياري)</span>
          </label>
          <textarea
            id="claim-note"
            rows={3}
            style={{ ...inputStyle, resize: "vertical" }}
            disabled={isSubmitting}
            {...register("note")}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>
            صورة إثبات <span style={fieldHint}>(اختياري)</span>
          </label>
          {!proofPreview ? (
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleFile}
              disabled={isSubmitting}
              style={{ ...inputStyle, padding: 8 }}
            />
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img
                src={proofPreview}
                alt="صورة الإثبات"
                width={64}
                height={64}
                style={{
                  borderRadius: "var(--ys-r)",
                  objectFit: "cover",
                  border: "1px solid var(--ys-rule)",
                }}
              />
              <button
                type="button"
                onClick={removeProof}
                disabled={isSubmitting}
                style={ghostBtnStyle}
              >
                إزالة
              </button>
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{ ...primaryBtnStyle, opacity: isSubmitting ? 0.6 : 1 }}
          >
            {isSubmitting ? "جارِ الإرسال…" : "إرسال الطلب"}
          </button>
          <button
            type="button"
            onClick={onBack}
            disabled={isSubmitting}
            style={ghostBtnStyle}
          >
            رجوع
          </button>
        </div>
      </form>
    </div>
  );
}
